import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useCreateReason,
  usePriorityLevel,
  useUpdateReason,
} from "@features/cx/settings/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface DisconnectionReason {
  id: number;
  configurationCode: string;
  name: string;
  longDescription: string;
  extraData: {
    acknowledgementTime: number;
    responseTime: number;
    resolutionTime: number;
    priorityLevel: number;
    requiredApproval: boolean;
  };
}

interface DisconnectionReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason?: DisconnectionReason | null;
  onClose: () => void;
  onSave?: (reasonData: DisconnectionReason) => void;
}

export function DisconnectionReasonDialog({
  open,
  onOpenChange,
  reason,
  onClose,
  onSave,
}: DisconnectionReasonDialogProps) {
  const createDisconnectionReason = useCreateReason();
  const updateDisconnectionReason = useUpdateReason();
  const { data: priorityData } = usePriorityLevel();
  const remoteUtilityId = useRemoteUtilityId();
  const [formData, setFormData] = useState({
    code: reason?.configurationCode || "",
    name: reason?.name || "",
    longDescription: reason?.longDescription || "",
    acknowledgementTime: reason?.extraData?.acknowledgementTime,
    responseTime: reason?.extraData?.responseTime,
    resolutionTime: reason?.extraData?.resolutionTime,
    priority: reason?.extraData?.priorityLevel,
    requiresApproval: reason?.extraData?.requiredApproval,
  });

  const getPriorityOptions = useMemo(() => {
    if (!priorityData || !Array.isArray(priorityData)) {
      return [];
    }
    return priorityData.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [priorityData]);

  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Reason Name",
      placeholder: "e.g., Non-payment",
      required: true,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Provide a descriptive name for the disconnect reason (e.g., “Non-payment,” “Meter Tampering”). This will be displayed to users when selecting a disconnect reason.",
    },
    {
      name: "code",
      type: "text",
      label: "Reason Code",
      placeholder: "e.g., NON_PAY",
      required: true,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Enter a unique code for the disconnect reason. This code is used for system reference, reporting, and integrations.",
    },
    {
      name: "longDescription",
      type: "textarea",
      label: "Description",
      placeholder: "Describe when this reason should be used...",
      rows: 3,
      className: "col-span-2",
      showHelperTooltip: true,
      tooltipText:
        "Provide a detailed description of what this disconnection reason covers, including typical scenarios, related issues, and resolution approaches.",
    },
    {
      name: "acknowledgementTime",
      type: "number",
      label: "Acknowledgement Time (Hours)",
      placeholder: "24",
      min: 1,
      required: false,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Maximum time allowed to acknowledge receipt of the complaint and send initial response to the customer. This is the first customer touchpoint.",
    },
    {
      name: "responseTime",
      type: "number",
      label: "Response Time (Hours)",
      placeholder: "48",
      min: 1,
      required: false,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Maximum time allowed to provide a substantive response or action plan to the customer after initial acknowledgment.",
    },
    {
      name: "resolutionTime",
      type: "number",
      label: "Resolution Time (Hours)",
      placeholder: "72",
      min: 1,
      required: false,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Maximum time allowed to fully resolve the complaint, including all necessary actions, approvals, and customer closure confirmation.",
    },
    {
      name: "priority",
      type: "select",
      label: "Priority",
      placeholder: "Select priority level",
      required: true,
      options: getPriorityOptions,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Severity level determines priority calculation, response times, and escalation procedures. Critical issues get immediate attention, while low severity issues follow standard processing.",
    },
    {
      name: "requiresApproval",
      type: "switch",
      label: "Requires Approval",
      helperText: "Whether this reason requires manager approval",
      className: "col-span-2",
    },
    {
      name: "visibleOnSelfServicePortal",
      type: "switch",
      label: "Visible on Self Service Portal",
      tooltipText:
        "When enabled, customers can see and request this disconnection directly from the self service portal.",
      helperText:
        "Allow customers to view and initiate a disconnection request from the self service portal",
      className: "col-span-2",
      showHelperTooltip: true,
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  useEffect(() => {
    if (open) {
      const initialData = {
        code: reason?.configurationCode || "",
        name: reason?.name || "",
        longDescription: reason?.longDescription || "",
        acknowledgementTime: reason?.extraData?.acknowledgementTime,
        responseTime: reason?.extraData?.responseTime,
        resolutionTime: reason?.extraData?.resolutionTime,
        priority: reason?.extraData?.priorityLevel,
        requiresApproval: reason?.extraData?.requiredApproval,
        visibleOnSelfServicePortal:
          reason?.extraData?.is_visible_on_self_portal === 1 ||
          reason?.extraData?.is_visible_on_self_portal === true ||
          reason?.extraData?.visibleOnSelfServicePortal ||
          false,
      };
      setFormData(initialData);
      form.reset(initialData);
    }
  }, [reason, open, form]);

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  }, []);

  const handleSubmit = (data: any) => {
    const operation = reason ? "update" : "create";
    switch (operation) {
      case "update":
        const payloadEdit = {
          id: reason?.id,
          name: data.name,
          configuration_code: data.code,
          long_description: data.longDescription,
          request_type: "Disconnect Permanent",
          remote_utility_id: Number(remoteUtilityId),
          extra_data: {
            priority_level: String(data.priority),
            acknowledgement_time: Number(data.acknowledgementTime),
            response_time: Number(data.responseTime),
            resolution_time: Number(data.resolutionTime),
            required_approval: data.requiresApproval ?? false,
            is_visible_on_self_portal: data.visibleOnSelfServicePortal ? 1 : 0,
          },
        };

        updateDisconnectionReason.mutateAsync(payloadEdit);
        break;

      case "create":
        const payloadAdd = {
          name: data.name,
          configuration_code: data.code,
          long_description: data.longDescription,
          request_type: "Disconnect Permanent",
          remote_utility_id: Number(remoteUtilityId),
          extra_data: {
            priority_level: String(data.priority),
            acknowledgement_time: Number(data.acknowledgementTime),
            response_time: Number(data.responseTime),
            resolution_time: Number(data.resolutionTime),
            required_approval: data.requiresApproval ?? false,
            is_visible_on_self_portal: data.visibleOnSelfServicePortal ? 1 : 0,
          },
        };

        createDisconnectionReason.mutateAsync(payloadAdd);
        break;
    }

    handleClose();
  };

  // Handle form reset
  const handleReset = () => {
    const resetData = {
      code: "",
      name: "",
      longDescription: "",
      acknowledgementTime: "",
      responseTime: "",
      resolutionTime: "",
      priority: "",
      requiresApproval: false,
    };
    setFormData(resetData);
    form.reset(resetData);
  };

  // Handle dialog close
  const handleClose = () => {
    handleReset();
    onClose();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {reason
              ? "Edit Disconnection Reason"
              : "Add New Disconnection Reason"}
          </DialogTitle>
          <DialogDescription>
            Configure the reason, timing requirements, and priority settings
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleClose}
            onFieldChange={handleFieldChange}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: reason ? "Update Reason" : "Add Reason",
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                form: "space-y-6",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                label: "block text-sm font-medium text-gray-700 mb-1",
                error: "text-sm text-red-600 mt-1",
                container: "space-y-1",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
